import React, { useCallback, useEffect, useState } from 'react';
import { StickyNote, Loader2, Send, Trash2, X, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { useAuth } from '@/contexts/AuthContext.jsx';
import { listarNotas, crearNota, eliminarNota } from '../lclOperaciones.service.js';

const MAX = 2000;

const fechaHora = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
};

/**
 * Notas internas de una operación LCL en curso.
 *
 * Bitácora del equipo: cada nota queda con fecha y autor, y sólo quien
 * la escribió puede borrarla. El cliente no las ve.
 */
export default function NotasOperacionDialog({ open, onOpenChange, lclRequest }) {
  const toast = useToast();
  const { user } = useAuth();
  const yo = user?._id || user?.id || null;

  const [notas, setNotas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState(null);

  const id = lclRequest?._id;

  const cargar = useCallback(async () => {
    if (!id) return;
    setCargando(true);
    try {
      const r = await listarNotas(id);
      if (r?.success) setNotas(r.data || []);
      else toast.error('No se pudieron cargar las notas', r?.message);
    } finally {
      setCargando(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (open) {
      setTexto('');
      cargar();
    }
  }, [open, cargar]);

  const agregar = async () => {
    const t = texto.trim();
    if (!t) return;
    setGuardando(true);
    try {
      const r = await crearNota(id, t);
      if (r?.success) {
        setNotas((n) => [r.data, ...n]);
        setTexto('');
      } else {
        toast.error('No se pudo agregar la nota', [].concat(r?.message || '').join(' · '));
      }
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (nota) => {
    setBorrando(nota._id);
    try {
      const r = await eliminarNota(nota._id);
      if (r?.success) setNotas((n) => n.filter((x) => x._id !== nota._id));
      else toast.error('No se pudo borrar', r?.message);
    } finally {
      setBorrando(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="flex max-h-[calc(100vh-2rem)] w-[95vw] max-w-2xl flex-col gap-0 overflow-hidden p-0"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-amber-100 p-2 text-amber-700">
              <StickyNote className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg">Notas de la operación</DialogTitle>
              <DialogDescription className="text-sm">
                {lclRequest?.codigo} · {lclRequest?.clienteSnapshot?.nombre || '—'}
              </DialogDescription>
            </div>
          </div>
          <Button variant="ghost" size="icon" aria-label="Cerrar" onClick={() => onOpenChange?.(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Nueva nota */}
        <div className="shrink-0 space-y-2 border-b p-4">
          <Textarea
            rows={3}
            value={texto}
            maxLength={MAX}
            placeholder="Escribe una nota para el equipo…"
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              // Ctrl/Cmd + Enter agrega sin ir al botón.
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) agregar();
            }}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" /> Interna: el cliente no la ve.
              <span className="ml-2">{texto.length}/{MAX}</span>
            </p>
            <Button size="sm" onClick={agregar} disabled={!texto.trim() || guardando}>
              {guardando ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Agregar nota
            </Button>
          </div>
        </div>

        {/* Bitácora */}
        <div className="min-h-[160px] flex-1 overflow-y-auto p-4">
          {cargando ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : notas.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Todavía no hay notas en esta operación.
            </p>
          ) : (
            <ul className="space-y-3">
              {notas.map((n) => {
                const propia = !n.autorId || n.autorId === yo;
                return (
                  <li key={n._id} className="group rounded-lg border bg-muted/30 p-3">
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>
                        <span className="font-medium text-foreground">
                          {n.autorNombre || 'Sin autor'}
                        </span>{' '}
                        · {fechaHora(n.createdAt)}
                      </span>
                      {propia && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          title="Borrar nota"
                          disabled={borrando === n._id}
                          onClick={() => borrar(n)}
                        >
                          {borrando === n._id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{n.texto}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
